<template>
  <fts-design-modal
    full-screen
    class="fts-document-design-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title">
        <font-awesome-icon
          class="design-modal-header-icon fts-file-pdf"
          :icon="['far', 'file-pdf']"
          v-if="formData.type === 'PDF'"
        />
        <font-awesome-icon
          class="design-modal-header-icon fts-file-excel"
          :icon="['far', 'file-alt']"
          v-else-if="formData.type === 'Spreadsheet'"
        />
        <font-awesome-icon
          class="design-modal-header-icon fts-file-html"
          :icon="['far', 'file-code']"
          v-else-if="formData.type === 'HTML'"
        />
        <font-awesome-icon
          class="design-modal-header-icon fts-file-default"
          :icon="['far', 'file']"
          v-else
        />

        <input 
          type="text" 
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}" 
          v-model="formData.name"
          spellcheck="false"
          ref="designerName">
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{ error("name") }}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Template</span>
      <span class="design-modal-header-description">
        design a formatted document for printing or export in the format of pdf
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- document design -->
      <div class="document-design-controls-container">
        <div class="document-design-controls">
          <div class="document-design-control document-design-locus">
            <fts-form-element
              :form-data="formData"
              :item="{
                displayName: 'Test Locus',
                key: 'testLocusId',
                fieldName: 'testLocus',
                type: 7,
                connectionId: moduleId,
                required: true,
              }"
              comfort-design
              :class="{ 'with-error': isPreviewError }"
            >
              <fts-dropdown
                slot="formElement"
                slot-scope="{ formData, item }"
                :form-data="formData"
                :item="item"
                comfort-design
                @input="updateTestLocus"
              >
                <div slot="option" slot-scope="option">
                  {{ option.optionName }}
                </div>
              </fts-dropdown>
            </fts-form-element>
            <div class="error-message" v-if="isPreviewError">
              Please select one entry before preview.
            </div>
          </div>

          <fts-btn
            outline
            color="primary"
            text-transform-init
            class="document-design-control"
            :loading="!isPreview && previewLoading"
            @click="previewDesign"
            style="
              min-width: calc(calc(1.5em + 0.875rem + 0.75rem + 0.75rem) * 1.5);
            "
          >
            {{ isPreview ? "Design" : "Preview" }}
          </fts-btn>
        </div>

        <div class="document-design-controls" v-if="isPreview">
          <!-- style -->
          <div class="document-design-control document-design-style">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: 'style',
                displayName: 'Template Style',
                icon: 'fts-icon-menu-caret-down',
              }"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item }"
                :form-data="formData"
                :item="item"
                :clearable="false"
                :options="templates"
                comfort-design
                @optionSelected="retrieveDocument"
              >
              </fts-select>
            </fts-form-element>
          </div>
          <!-- color 1 -->
          <div class="document-design-control document-design-color">
            <swatches
              v-model="formData.color1"
              row-length="5"
              :swatches="colors"
              show-fallback
              fallback-input-type="color"
              shapes="circles"
              popover-x="right"
              :swatch-size="25"
              :trigger-style="{
                width: '25px',
                height: '25px',
              }"
              @input="updateTemplateColor"
              class="document-design-color-picker"
            />
            <div class="document-design-color-label">Primary Color</div>
          </div>
          <!-- color 2 -->
          <div class="document-design-control document-design-color">
            <swatches
              v-model="formData.color2"
              row-length="5"
              show-fallback
              fallback-input-type="color"
              :swatches="colors"
              shapes="circles"
              popover-x="right"
              :swatch-size="25"
              :trigger-style="{
                width: '25px',
                height: '25px',
              }"
              @input="updateTemplateColor"
              class="document-design-color-picker"
            />
            <div class="document-design-color-label">Secondary Color</div>
          </div>
          <!-- refresh -->
          <fts-btn
            text
            icon
            class="document-design-control"
            :loading="previewLoading"
            @click="retrieveDocument"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </fts-btn>
        </div>
      </div>
      <div
        class="design-modal-form-group"
        style="height: 100%"
        v-if="isPreview"
      >
        <div class="design-modal-form-label">Document Preview</div>
        <vue-pdf-app :fileName="fileName" :pdf="previewDocument" :config="config" />
      </div>
      <template v-else>
        <fts-tabs :initialTab="initialTab" v-if="beta">
          <fts-tab title="Simple">
            <fts-form-designer
              document
              :document-data="formData"
              :form="formData.design"
              class="fts-scrollbar"
            />
          </fts-tab>
          <!-- <fts-tab title="Expression">
            <fts-template-editor
              :type="'text'"
              :information="information"
              v-model="formData.html"
            />
          </fts-tab> -->
          <fts-tab title="Code" class="design-modal-form-group">
            <fts-script-editor
              editor-id="documentHtml"
              v-model="formData.html"
            />
          </fts-tab>
        </fts-tabs>
        <fts-form-designer
          document
          :document-data="formData"
          :form="formData.design"
          class="fts-scrollbar"
          v-else
        />
      </template>
      <!-- advanced -->
      <fts-collapsible-element :condition="advanced" v-if="beta">
        <fts-btn
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          text
          text-transform-init
          :class="{ open: isVisible }"
          class="design-modal-advanced-toggle"
          @click="toggle()"
        >
          Advanced settings
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            class="design-modal-advanced-toggle-icon"
          />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <div class="fts-col">
            <div class="fts-row gutters">
              <!-- type -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'type',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
                class="fts-col"
                v-if="beta"
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  reduce-key="name"
                  :options="types"
                  comfort-design
                  @optionSelected="updateType"
                  @clearSelection="updateType"
                >
                </fts-select>
              </fts-form-element>
              <!-- data source -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'dataSource',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
                class="fts-col"
                v-if="beta"
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  reduce-key="name"
                  :options="dataSources"
                  comfort-design
                  @optionSelected="updateDataSource"
                  @clearSelection="updateDataSource"
                >
                </fts-select>
              </fts-form-element>
              <!-- limit -->
              <fts-form-element
                :formData="formData"
                :item="{
                  displayName: 'Limit (default: 500)',
                  fieldName: 'limit',
                  type: 2,
                }"
                comfort-design
                class="fts-col"
              />
              <!-- filename -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'fileName',
                  type: 0,
                }"
                comfort-design
                class="fts-col"
              />
              <!-- subject -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'subject',
                  type: 0,
                }"
                comfort-design
                class="fts-col"
              />
            </div>
          </div>

          <div class="fts-col" v-if="beta">
            <div class="fts-row gutters">
              <!-- logic service name -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'logicServiceName',
                  type: 0,
                  required: formData.dataSource === 'Logic Service',
                }"
                :validator="$v.formData"
                comfort-design
                class="fts-col"
              />
              <!-- includes -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'includes',
                  type: 0,
                }"
                comfort-design
                class="fts-col"
              />
              <!-- list -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'list',
                  type: 0,
                }"
                comfort-design
                class="fts-col"
              />
            </div>
          </div>

          <div class="fts-col">
            <div class="fts-row gutters">
              <!-- menus -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'menus',
                  displayName: 'Listing',
                  icon: 'fts-icon-menu-caret-down',
                }"
                comfort-design
                class="fts-col"
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  :options="menus"
                  multiple
                  comfort-design
                >
                  <fts-label-group
                    slot="selected-option-container"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="menus"
                    emoji
                  />
                  <fts-label-group
                    slot="option"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="menus"
                    emoji
                  />
                </fts-select>
              </fts-form-element>
            </div>
          </div>

          <div class="fts-col">
            <div class="fts-row gutters">
              <!-- condition -->
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'condition',
                  icon: 'fts-icon-text',
                }"
                comfort-design
                class="fts-col"
              >
                <fts-script-editor
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  editor-id="condition"
                  v-model="formData[item.fieldName]"
                  lang="js"
                />
              </fts-form-element>
            </div>
          </div>

          <div class="fts-col">
            <div class="fts-row gutters">
              <!-- report -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'report',
                  input: 'toggleSwitch',
                }"
                comfort-design
                class="fts-col"
              />
              <!-- internal -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'xsls',
                  displayName: 'XSLS',
                  input: 'toggleSwitch',
                }"
                comfort-design
                class="fts-col"
              />
              <!-- allow selection -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'allowSelection',
                  input: 'toggleSwitch',
                }"
                comfort-design
                class="fts-col"
              />
              <!-- disable -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'disable',
                  input: 'toggleSwitch',
                }"
                comfort-design
                class="fts-col"
              />
              <!-- manual -->
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'manual',
                  input: 'toggleSwitch',
                }"
                comfort-design
                class="fts-col"
              />
              <!-- version -->
              <fts-form-element
                :formData="formData"
                :item="{
                  displayName: 'Version',
                  fieldName: 'version',
                  type: 3,
                }"
                comfort-design
                class="fts-col"
              />
            </div>
          </div>

          <!-- user input form -->
          <fts-collapsible-element
            v-model="formData.hasUserInput"
            class="fts-col"
          >
            <fts-btn
              slot="toggle"
              slot-scope="{ isVisible, toggle }"
              text
              text-transform-init
              :class="{ open: isVisible }"
              class="design-modal-advanced-toggle"
              @click="toggle()"
            >
              User input form
              <font-awesome-icon
                :icon="['fas', 'caret-right']"
                class="design-modal-advanced-toggle-icon"
              />
            </fts-btn>
            <template #content>
              <fts-form-designer
                slot="content"
                :form="formData.userInput"
                :document-data="userInputFormData"
                preview-design-action
                class="design-modal-form-group design-modal-advanced-content fts-scrollbar"
              />
              <fts-form-script :form-data="formData.userInput" />
            </template>
          </fts-collapsible-element>
        </div>
      </fts-collapsible-element>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";
import Swatches from "vue-swatches";
import VueJsonPretty from "vue-json-pretty";
import VuePdfApp from "vue-pdf-app";
// import this to use default icons for buttons
// import "vue-pdf-app/dist/icons/main.css";

import commons from "../commons/commons";
import validator from "../commons/designValidator";

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSScriptEditor from "./FTSScriptEditor";
import FTSTemplateEditor from "./FTSTemplateEditor";
import FTSTabs from "../elements/tabs/FTSTabs";
import FTSTab from "../elements/tabs/FTSTab";
import FTSDropdown from "../elements/FTSDropdown";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSFormScript from "./FTSFormScript";

export default {
  components: {
    swatches: Swatches,
    VueJsonPretty,
    VuePdfApp,
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-form-designer": FTSFormDesigner,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-script-editor": FTSScriptEditor,
    "fts-template-editor": FTSTemplateEditor,
    "fts-tabs": FTSTabs,
    "fts-tab": FTSTab,
    "fts-dropdown": FTSDropdown,
    "fts-contenteditable": FTSContenteditable,
    "fts-form-script": FTSFormScript,
  },
  data: () => ({
    fileName: 'testing123.pdf',
    config: {
      sidebar: false,
      toolbar: {
        toolbarViewerLeft: {
          findbar: false,
          previous: false,
          next: false
        },
        toolbarViewerRight: {
          presentationMode: false,
          openFile: false,
          viewBookmark: false
        },
      },
      secondaryToolbar: false
    },
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    advanced: false,
    userInputFormData: {},
    previewDocument: null,
    isPreview: false,
    isPreviewError: false,
    previewLoading: false,
    numPages: 0,
    initialTab: 0,
    types: [
      { id: "pdf", name: "PDF" },
      { id: "file", name: "File" },
      { id: "html", name: "HTML" },
      { id: "spreadsheet", name: "Spreadsheet" },
    ],
    dataSources: [
      { id: "entry", name: "Entry" },
      { id: "collection", name: "Entries" },
      { id: "logicService", name: "Logic Service" },
      { id: "custom", name: "Custom" },
    ],
    templates: [
      { id: "basic", name: "basic" },
      { id: "style01", name: "style01" },
      { id: "style02", name: "style02" },
      { id: "style03", name: "style03" },
    ],
  }),
  validations() {
    let formData = {};
    formData = validator.documentDesign(this.documents);
    return { formData };
  },
  watch: {
    "formData.hasUserInput"(val) {
      if (val) {
        if (!this.formData.userInput || this.formData.userInput.length)
          this.$set(this.formData, "userInput", {
            rows: [{ items: [] }],
          });
      }
    },
  },
  methods: {
    async show(item, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(item);

      if (this.formData.html) 
        this.initialTab = 1;

      if (this.formData.design && this.formData.design.rows) {
        commons.expandWorkDesign(this.information, this.formData.design);
      } else {
        this.$set(this.formData, "design", {
          rows: [{ items: [] }],
        });
      }

      if (!this.formData.type) {
        this.$set(this.formData, "type", this.types[0].name);
        this.$set(this.formData, "typeId", this.types[0].id);
      }

      if (!this.formData.dataSource) {
        this.$set(this.formData, "dataSource", this.dataSources[0].name);
        this.$set(this.formData, "dataSourceId", this.dataSources[0].id);
      }

      if (!this.formData.condition) 
        this.$set(this.formData, "condition", "");

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      });
    },
    done(save) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (save) {
          this.saveCallBack && this.saveCallBack(this.formData);
          this.originalData = _.cloneDeep(this.formData);
        } else {
          this.resolve && this.resolve(this.formData);
          this.reset();
        }
      } else {
        console.warn(
          "DocumentDesign:InvalidForm", 
          validator.translateValidations(this.$v.formData, true)
        );

        try {
          if (this.$v.formData.logicServiceName.required === false)
            this.$toasted.error('Logic Service Name is required');
        } catch(err) {
          console.warn("DocumentDesign:Done", err);
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      this.advanced = false;
      this.userInputFormData = {};
      this.previewDocument = null;
      this.isPreview = false;
      this.isPreviewError = false;
      this.previewLoading = false;
      this.numPages = 0;
      this.initialTab = 0;
    },
    error(key) {
      return validator.documentDesignError(this.$v.formData, key);
    },
    updateType(item) {
      if (item && item.id) this.$set(this.formData, "typeId", item.id);
      else if (this.formData.typeId) this.$delete(this.formData, "typeId");
    },
    updateDataSource(item) {
      if (item && item.id) this.$set(this.formData, "dataSourceId", item.id);
      else if (this.formData.dataSourceId)
        this.$delete(this.formData, "dataSourceId");
    },
    updateTestLocus() {
      if (this.formData.testLocus && this.isPreviewError)
        this.isPreviewError = false;
      if (this.isPreview) this.retrieveDocument();
    },
    updateTemplateColor: _.debounce(function() {
      this.retrieveDocument();
    }, 800),
    async previewDesign() {
      if (!this.isPreview) {
        // set style
        if (!this.formData.color1)
        this.$set(this.formData, "color1", "#2b9ec6");
        if (!this.formData.color2)
          this.$set(this.formData, "color2", "#a4ce3a");
        if (!this.formData.style)
          this.$set(this.formData, "style", this.templates[0].id);
        // retrieve
        if (this.formData.testLocus) {
          await this.retrieveDocument();
          this.isPreview = true;
        } else {
          this.isPreviewError = true;
        }
      } else {
        this.isPreview = false;
      }
    },
    async retrieveDocument() {
      if (this.formData.testLocus) {
        this.previewLoading = true;
        try {
          let response = await this.$store.dispatch(
            "testDocument",
            this.formData
          );
          function _base64ToArrayBuffer(base64) {
            var binary_string = window.atob(base64);
            var len = binary_string.length;
            var bytes = new Uint8Array(len);
            for (var i = 0; i < len; i++) {
              bytes[i] = binary_string.charCodeAt(i);
            }
            return bytes.buffer;
          }
          this.previewDocument = _base64ToArrayBuffer(response.data);

          if (!this.isPreview) this.isPreview = true;
        } catch (err) {
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            this.$toasted.error(
              "Failed to retrieve template: " + err.response.data.message
            );
          } else {
            console.error(err);
            this.$toasted.error("Failed to retrieve template.");
          }
        } finally {
          this.previewLoading = false;
        }
      } else {
        this.isPreviewError = true;
      }
    },
  },
  computed: {
    beta() {
      return this.$store.state.beta;
    },
    moduleId() {
      return this.$store.state.vector.id;
    },
    documents() {
      return this.$store.getters.canvasDocuments;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    menus() {
      return this.$store.getters.canvasMenus.map((x) => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji,
      }));
    },
    colors() {
      return commons.darkPalette;
    },
    isExist() {
      return Boolean(this.documents.find((x) => x.id === this.formData.id));
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.pdf-app .pdfViewer .page {
  box-sizing: content-box;
}

.fts-document-design-modal {
  .document-design-controls-container {
    @include flex-gap($gapTop: 1.5rem, $gapLeft: 1rem);
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 2rem;
    .document-design-controls {
      display: flex;
      align-items: flex-end;
      .document-design-control {
        position: relative;
        &:not(:last-child) {
          margin-right: 2rem;
        }
        .fts-form-group {
          margin-bottom: 0;
        }
        .error-message {
          position: absolute;
          margin-top: 0.175rem;
          font-size: 0.75rem;
          color: $dangerColor;
        }
      }
      .document-design-locus {
        width: 500px;
      }
      .document-design-style {
        width: 280px;
      }
      .document-design-color {
        display: flex;
        align-items: center;
        .document-design-color-picker {
          display: inline-flex;
          margin-right: 0.75rem;
          .vue-swatches__trigger {
            border: 1px solid $borderColor;
          }
        }
        .document-design-color-label {
          font-size: 0.875rem;
        }
      }
    }
  }
  .document-design-pdf-preview {
    height: 100%;
    padding: 50px;
    background-color: #eee;
  }

  .fts-form-designer {
    > .form-designer-content {
      max-height: 800px;
    }
  }
}

.pdf-app .toolbar {
  z-index: 40;
}
</style>